import React, { useState, useEffect, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { useDevices } from '../hooks/useDevices'
import { 
  createReplenishSession,
  getActiveSession, 
  terminateSession, 
  extendSession,
  shouldShowWarning,
  getRemainingMinutes,
  getTimeRemaining,
  validateSessionAccess
} from '../services/replenishSessionService'
import { doc, updateDoc, getDoc } from 'firebase/firestore'
import { db } from '../firebase'
import LoadingSpinner from './LoadingSpinner'
import ProductSearchInput from './ProductSearchInput'
import MessagesService from '../services/messagesService'
import { validateImageUrl } from '../utils/imageValidation'
import { ROLES } from '../services/permissionService'

const ReplenishInterface = () => {
  const { machineId } = useParams()
  const navigate = useNavigate()
  const { currentUser, userClaims, userPermissions, permissionsLoading, hasPermission, PERMISSIONS } = useAuth()
  const { devices, getDeviceById, loading: devicesLoading, error: devicesError } = useDevices()
  
  // DEBUG: Component mounted
  console.log('🔍 ReplenishInterface mounted for machine:', machineId)
  console.log('🔍 userClaims:', userClaims)
  console.log('🔍 currentUser:', currentUser?.email)
  
  const [device, setDevice] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [session, setSession] = useState(null)
  const [showWarning, setShowWarning] = useState(false)
  const [tempLayoutData, setTempLayoutData] = useState({})
  const [layoutRows, setLayoutRows] = useState(6)
  const [layoutCols, setLayoutCols] = useState(10)
  const [rowConfigs, setRowConfigs] = useState([5,5,6,10,10,10])
  const [editingSlot, setEditingSlot] = useState(null)
  const [slotFormData, setSlotFormData] = useState({ 
    name: '', price: '', quantity: '', capacity: '', imageUrl: '', slotNumber: '', row: 0, column: 0, expiry: '' 
  })
  const [saving, setSaving] = useState(false)
  const [layoutMode, setLayoutMode] = useState('image')
  const [maintenanceMode, setMaintenanceMode] = useState(false)
  const [updatingMaintenance, setUpdatingMaintenance] = useState(false)
  const [imageUrlError, setImageUrlError] = useState('')
  const [endingSession, setEndingSession] = useState(false)
  const leavingSessionRef = useRef(false)

  // Function to update device layout in Firebase
  const updateDeviceLayout = async (deviceId, layoutData) => {
    try {
      const deviceRef = doc(db, `${userClaims?.customer}_machines`, deviceId)
      await updateDoc(deviceRef, layoutData)
      return true
    } catch (error) {
      console.error('Error updating device layout:', error)
      return false
    }
  }

  // Session monitoring effect
  useEffect(() => {
    const monitorSession = () => {
      if (session && !leavingSessionRef.current) {
        const warning = shouldShowWarning(session)
        setShowWarning(warning)
        
        const remaining = getRemainingMinutes(session)
        if (remaining <= 0) {
          handleSessionExpired()
        }
      }
    }

    const interval = setInterval(monitorSession, 30000) // Check every 30 seconds
    return () => clearInterval(interval)
  }, [session])

  // Initialize component
  useEffect(() => {
    let isCurrent = true
    leavingSessionRef.current = false

    initializeReplenishInterface(() => isCurrent && !leavingSessionRef.current)

    return () => {
      isCurrent = false
    }
  }, [machineId, currentUser, userClaims, userPermissions, permissionsLoading])

  const initializeReplenishInterface = async (isCurrent = () => true) => {
    try {
      if (!isCurrent()) return

      setLoading(true)
      setError(null)

      // DEBUG: Log all relevant values
      console.log('=== REPLENISH INTERFACE DEBUG ===')
      console.log('Machine ID from URL:', machineId)
      console.log('Current User:', currentUser?.email)
      console.log('User Claims:', userClaims)
      console.log('User Permissions:', userPermissions)
      console.log('Collection name would be:', userClaims?.customer + '_machines')
      console.log('=====================================')

      // Check authentication
      if (!currentUser) {
        console.log('No current user, redirecting to login')
        navigate('/login', { state: { returnUrl: `/replenish/${machineId}` } })
        return
      }

      // Wait for claims and permissions to finish loading — don't treat "not loaded yet" as "denied"
      if (!userClaims || permissionsLoading) {
        // Claims not yet parsed OR permissions still fetching; keep spinner — effect re-runs on each change
        return
      }
      if (userPermissions === null) {
        // Claims parsed, loading done, but permissions definitively unavailable
        setError('Unable to load your permissions. Please refresh the page and try again.')
        setLoading(false)
        return
      }

      // Check basic replenish permission
      if (!hasPermission(PERMISSIONS.DEVICES.REPLENISH)) {
        setError('You do not have permission to access the replenishment interface.')
        setLoading(false)
        return
      }

      // Load device data directly from Firebase (bypassing useDevices hook timing issues)
      console.log('Loading device directly from Firebase with:', machineId)
      const deviceRef = doc(db, `${userClaims.customer}_machines`, machineId)
      const deviceSnap = await getDoc(deviceRef)

      if (!isCurrent()) return
      
      let deviceData = null
      if (deviceSnap.exists()) {
        deviceData = {
          id: deviceSnap.id,
          ...deviceSnap.data()
        }
      }
      console.log('Device data result:', deviceData)
      
      if (!deviceData) {
        console.log('Device data is null/undefined')
        setError('Machine not found or you do not have access to this machine.')
        setLoading(false)
        return
      }

      // Create replenishment session for this machine
      const sessionResult = await createReplenishSession(
        { 
          ...currentUser, 
          customClaims: userClaims 
        }, 
        userPermissions, 
        machineId, 
        userClaims?.customer
      )

      if (!isCurrent()) {
        if (sessionResult.success) {
          await terminateSession(sessionResult.sessionId)
        }
        return
      }

      if (!sessionResult.success) {
        setError(`Access denied: ${sessionResult.error}`)
        setLoading(false)
        return
      }

      // Store session data
      setSession({
        sessionId: sessionResult.sessionId,
        machineId: sessionResult.machineId,
        expiresAt: sessionResult.expiresAt,
        warningAt: sessionResult.warningAt
      })

      setDevice(deviceData)
      
      // Initialize layout data
      const slots = deviceData.slots || {}
      setTempLayoutData(slots)
      
      // Set layout dimensions (same as admin portal)
      setLayoutRows(deviceData.rows || deviceData.grid_rows || 6)
      setLayoutCols(deviceData.cols || deviceData.grid_cols || 10)
      setRowConfigs(deviceData.row_configs || [5,5,6,10,10,10])
      setLayoutMode(deviceData.layout_mode || 'image')
      setMaintenanceMode(deviceData.maintenance_mode || false)
      
      console.log('=== LAYOUT DEBUG ===')
      console.log('Device rows:', deviceData.rows)
      console.log('Device cols:', deviceData.cols) 
      console.log('Device row_configs:', deviceData.row_configs)
      console.log('Final layoutRows:', deviceData.rows || deviceData.grid_rows || 6)
      console.log('Final layoutCols:', deviceData.cols || deviceData.grid_cols || 10)
      console.log('Final rowConfigs:', deviceData.row_configs || [5,5,6,10,10,10])
      console.log('Total slots count:', Object.keys(deviceData.slots || {}).length)

      setLoading(false)
    } catch (err) {
      if (!isCurrent()) return
      console.error('Error initializing replenish interface:', err)
      setError('Failed to load machine data. Please try again.')
      setLoading(false)
    }
  }

  const handleSessionExpired = () => {
    if (leavingSessionRef.current) return

    leavingSessionRef.current = true
    setSession(null)
    setShowWarning(false)
    alert('Your replenishment session has expired. You will be redirected to the login page.')
    navigate('/login', { state: { returnUrl: `/replenish/${machineId}` }, replace: true })
  }

  const handleExtendSession = async () => {
    if (!session) return
    
    try {
      const result = await extendSession(session.sessionId, 15)
      if (result.success) {
        setSession({
          ...session,
          expiresAt: result.expiresAt,
          warningAt: result.warningAt
        })
        setShowWarning(false)
      }
    } catch (error) {
      console.error('Error extending session:', error)
    }
  }

  const handleEndSession = async () => {
    if (endingSession || leavingSessionRef.current) return

    const sessionToClose = session
    const userRoles = userPermissions?.roles || []
    const hasReplenishWorkerRole = userRoles.includes(ROLES.REPLENISH_WORKER)
    const destination = hasReplenishWorkerRole ? '/replenish-worker' : '/dashboard'

    leavingSessionRef.current = true
    setEndingSession(true)
    setSession(null)
    setShowWarning(false)

    try {
      if (sessionToClose?.sessionId) {
        const terminated = await terminateSession(sessionToClose.sessionId)
        if (!terminated) {
          console.warn('Unable to remove replenishment session before leaving the interface')
        }
      }
    } catch (error) {
      console.error('Error ending replenishment session:', error)
    } finally {
      navigate(destination, { replace: true })
    }
  }

  const isSlotExpired = (slot) => {
    if (!slot?.expiry || !device?.expiry_tracking_enabled) return false
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    const expiryDate = new Date(slot.expiry)
    return expiryDate < today
  }

  // Layout editing functions
  const getDefaultSlotNumber = (row, col) => {
    return (row * 10) + col + 10
  }

  const handleSlotEdit = (row, col, slotId, slot) => {
    setEditingSlot({ row, col, slotId })
    setSlotFormData({
      name: slot?.name || '',
      price: slot?.price || '',
      quantity: slot?.quantity || '',
      capacity: slot?.capacity || slot?.quantity || '',
      imageUrl: slot?.imageUrl || '',
      slotNumber: slotId || getDefaultSlotNumber(row, col).toString(),
      row: row,
      column: col,
      expiry: slot?.expiry || ''
    })
  }

  const handleSlotSave = () => {
    // Validate image URL before saving
    if (imageUrlError) {
      alert('Please fix the image URL error before saving.')
      return
    }
    
    // Validate quantity and capacity are not negative
    const qty = parseInt(slotFormData.quantity) || 0
    const cap = parseInt(slotFormData.capacity) || 0
    if (qty < 0 || cap < 0) {
      alert('Quantity and capacity must be zero or greater.')
      return
    }
    
    // Validate quantity doesn't exceed capacity (when capacity is set)
    if (cap > 0 && qty > cap) {
      alert('Current quantity cannot exceed max capacity.')
      return
    }
    
    const updatedSlots = { ...tempLayoutData }
    const slotNumber = slotFormData.slotNumber || getDefaultSlotNumber(editingSlot.row, editingSlot.col).toString()
    
    if (editingSlot.slotId) {
      // Update existing slot
      if (editingSlot.slotId !== slotNumber) {
        delete updatedSlots[editingSlot.slotId]
      }
      updatedSlots[slotNumber] = {
        ...updatedSlots[editingSlot.slotId],
        name: slotFormData.name,
        price: parseFloat(slotFormData.price) || 0,
        quantity: parseInt(slotFormData.quantity) || 0,
        capacity: parseInt(slotFormData.capacity) || parseInt(slotFormData.quantity) || 0,
        imageUrl: slotFormData.imageUrl,
        row: editingSlot.row,
        column: editingSlot.col,
        expiry: slotFormData.expiry || ''
      }
    } else {
      // Create new slot
      updatedSlots[slotNumber] = {
        name: slotFormData.name,
        price: parseFloat(slotFormData.price) || 0,
        quantity: parseInt(slotFormData.quantity) || 0,
        capacity: parseInt(slotFormData.capacity) || parseInt(slotFormData.quantity) || 0,
        imageUrl: slotFormData.imageUrl,
        row: editingSlot.row,
        column: editingSlot.col,
        expiry: slotFormData.expiry || ''
      }
    }
    
    setTempLayoutData(updatedSlots)
    setEditingSlot(null)
    setSlotFormData({ name: '', price: '', quantity: '', capacity: '', imageUrl: '', slotNumber: '', row: 0, column: 0, expiry: '' })
    setImageUrlError('')
  }

  const handleSlotDelete = () => {
    if (editingSlot.slotId) {
      const updatedSlots = { ...tempLayoutData }
      delete updatedSlots[editingSlot.slotId]
      setTempLayoutData(updatedSlots)
    }
    setEditingSlot(null)
    setSlotFormData({ name: '', price: '', quantity: '', capacity: '', imageUrl: '', slotNumber: '', row: 0, column: 0, expiry: '' })
    setImageUrlError('')
  }

  // Handle product selection from search dropdown
  const handleProductSelect = (product) => {
    if (product) {
      setSlotFormData(prev => ({
        ...prev,
        name: product.displayname || product.name || '',
        price: product.MRP || prev.price,
        imageUrl: product.imageUrl || prev.imageUrl
      }))
    }
  }

  const handleSaveLayout = async () => {
    try {
      setSaving(true)
      
      // Validate session before allowing any changes
      if (!session) {
        setError('No active session. Please refresh and try again.')
        setSaving(false)
        return
      }

      const activeSession = await getActiveSession(session.sessionId, machineId)
      if (!activeSession) {
        alert('Your session has expired. Please scan the QR code again to continue.')
        handleSessionExpired()
        return
      }

      // Validate session access
      if (!validateSessionAccess(currentUser, userPermissions, activeSession)) {
        setError('Session validation failed. Access denied.')
        setSaving(false)
        return
      }
      
      const layoutData = {
        slots: tempLayoutData,
        grid_rows: layoutRows,
        grid_cols: layoutCols,
        row_configs: rowConfigs,
        updated_at: new Date().toISOString(),
        updated_by: currentUser.email,
        session_id: session.sessionId, // Track which session made the change
        layout_mode: layoutMode,
        maintenance_mode: maintenanceMode // Sync maintenance mode when saving
      }

      const success = await updateDeviceLayout(machineId, layoutData)
      
      if (success) {
        // Log activity to messages collection
        try {
          console.log('🔍 LAYOUT SAVE - About to log activity')
          console.log('🔍 LAYOUT SAVE - userClaims:', userClaims)
          console.log('🔍 LAYOUT SAVE - customer:', userClaims?.customer)
          console.log('🔍 LAYOUT SAVE - currentUser:', currentUser?.email)
          
          if (!userClaims?.customer) {
            console.error('❌ LAYOUT SAVE - Cannot log activity: userClaims.customer is undefined!')
            throw new Error('Customer information not available for activity logging')
          }
          
          const messagesService = new MessagesService(userClaims.customer)
          await messagesService.writeMessage({
            type: 'Layout updated',
            deviceId: machineId,
            source: 'replenish-app',
            userId: currentUser?.uid,
            userEmail: currentUser?.email,
            additionalData: {
              rows: layoutRows,
              cols: layoutCols,
              layoutMode: layoutMode,
              slotCount: Object.keys(tempLayoutData).length,
              sessionId: session?.sessionId,
              timestamp: new Date().toISOString()
            }
          })
          console.log(`✅ Replenish activity logged: Layout updated for device ${machineId}`)
        } catch (logError) {
          console.error('❌ Failed to log replenish layout update activity:', logError)
          // Don't fail the operation if logging fails
        }
        
        alert('Layout saved successfully! Maintenance mode and all changes have been synced to the machine.')
        setDevice(prev => ({ ...prev, slots: tempLayoutData, layout_mode: layoutMode, maintenance_mode: maintenanceMode }))
      } else {
        alert('Failed to save layout. Please try again.')
      }
    } catch (error) {
      console.error('Error saving layout:', error)
      alert('An error occurred while saving. Please try again.')
    } finally {
      setSaving(false)
    }
  }

  const getRowColumnCount = (row) => {
    return (Array.isArray(rowConfigs) && rowConfigs[row]) || layoutCols
  }

  // Restock all slots to their capacity (same as admin portal)
  const restockAllSlots = async () => {
    const slots = tempLayoutData
    const eligibleSlots = Object.entries(slots).filter(([key, slot]) => {
      const isLockedOut = slot.slot_lockout === true || slot.slock_lockout === true
      return !isLockedOut && slot.capacity > 0 && slot.quantity < slot.capacity
    })

    if (eligibleSlots.length === 0) {
      alert('No slots available for restocking. All functional slots are either at capacity or have no capacity set.')
      return
    }

    const restockSummary = eligibleSlots.map(([key, slot]) => 
      `Slot ${key}: ${slot.quantity} → ${slot.capacity} (+${slot.capacity - slot.quantity})`
    ).join('\n')

    if (!window.confirm(`Restock ${eligibleSlots.length} slots to their full capacity?\n\nSlots to restock:\n${restockSummary}\n\nLocked-out slots will be skipped.`)) {
      return
    }

    setSaving(true)
    try {
      const updatedSlots = { ...tempLayoutData }
      let restockedCount = 0
      let totalAddedItems = 0
      
      // Restock all eligible slots
      eligibleSlots.forEach(([key, slot]) => {
        const itemsAdded = slot.capacity - slot.quantity
        updatedSlots[key] = {
          ...slot,
          quantity: slot.capacity
        }
        restockedCount++
        totalAddedItems += itemsAdded
      })

      setTempLayoutData(updatedSlots)
      
      // Log activity to messages collection
      try {
        console.log('🔍 RESTOCK - About to log activity')
        console.log('🔍 RESTOCK - userClaims:', userClaims)
        console.log('🔍 RESTOCK - customer:', userClaims?.customer)
        console.log('🔍 RESTOCK - currentUser:', currentUser?.email)
        
        if (!userClaims?.customer) {
          console.error('❌ RESTOCK - Cannot log activity: userClaims.customer is undefined!')
          throw new Error('Customer information not available for activity logging')
        }
        
        const messagesService = new MessagesService(userClaims.customer)
        await messagesService.writeMessage({
          type: 'Inventory restocked',
          deviceId: machineId,
          source: 'replenish-app',
          userId: currentUser?.uid,
          userEmail: currentUser?.email,
          additionalData: {
            restockedCount: restockedCount,
            totalAddedItems: totalAddedItems,
            sessionId: session?.sessionId,
            timestamp: new Date().toISOString()
          }
        })
        console.log(`✅ Replenish activity logged: Restocked ${restockedCount} slots`)
      } catch (logError) {
        console.error('❌ Failed to log replenish restock activity:', logError)
        // Don't fail the operation if logging fails
      }
      
      alert(`Successfully restocked ${restockedCount} slots with ${totalAddedItems} total items.`)
    } catch (error) {
      console.error('Error restocking slots:', error)
      alert('Failed to restock slots. Please try again.')
    } finally {
      setSaving(false)
    }
  }

  // Clear all slot lockouts (same as admin portal)
  const clearAllSlotLockouts = async () => {
    if (!window.confirm('Are you sure you want to clear all slot lockouts? This will make all slots functional again.')) {
      return
    }

    setSaving(true)
    try {
      const updatedSlots = { ...tempLayoutData }
      let clearedCount = 0
      
      // Clear lockout status for all slots
      Object.keys(updatedSlots).forEach(key => {
        const slot = updatedSlots[key]
        if (slot.slot_lockout === true || slot.slock_lockout === true) {
          clearedCount++
        }
        // Clear both field variations to ensure compatibility
        updatedSlots[key] = {
          ...slot,
          slot_lockout: false,
          slock_lockout: false
        }
      })

      if (clearedCount === 0) {
        alert('No locked slots found to clear.')
        setSaving(false)
        return
      }

      // Save to database
      const success = await updateDeviceLayout(machineId, { slots: updatedSlots })
      
      if (success) {
        // Update local state
        setTempLayoutData(updatedSlots)
        setDevice(prev => ({ ...prev, slots: updatedSlots }))
        
        // Log activity to messages collection
        try {
          console.log('🔍 CLEAR LOCKOUTS - About to log activity')
          console.log('🔍 CLEAR LOCKOUTS - userClaims:', userClaims)
          console.log('🔍 CLEAR LOCKOUTS - customer:', userClaims?.customer)
          console.log('🔍 CLEAR LOCKOUTS - currentUser:', currentUser?.email)
          
          if (!userClaims?.customer) {
            console.error('❌ CLEAR LOCKOUTS - Cannot log activity: userClaims.customer is undefined!')
            throw new Error('Customer information not available for activity logging')
          }
          
          const messagesService = new MessagesService(userClaims.customer)
          await messagesService.writeMessage({
            type: 'Slot lockouts cleared',
            deviceId: machineId,
            source: 'replenish-app',
            userId: currentUser?.uid,
            userEmail: currentUser?.email,
            additionalData: {
              clearedCount: clearedCount,
              sessionId: session?.sessionId,
              timestamp: new Date().toISOString()
            }
          })
          console.log(`✅ Replenish activity logged: Cleared ${clearedCount} slot lockouts`)
        } catch (logError) {
          console.error('❌ Failed to log replenish lockout clearing activity:', logError)
          // Don't fail the operation if logging fails
        }
        
        alert(`Successfully cleared lockouts for ${clearedCount} slots.`)
      } else {
        alert('Failed to clear slot lockouts. Please try again.')
      }
    } catch (error) {
      console.error('Error clearing slot lockouts:', error)
      alert('Failed to clear slot lockouts. Please try again.')
    } finally {
      setSaving(false)
    }
  }

  // Toggle layout mode between image and slot
  const toggleLayoutMode = () => {
    const newMode = layoutMode === 'image' ? 'slot' : 'image'
    setLayoutMode(newMode)
  }

  // Toggle maintenance mode and sync to Firestore immediately
  const toggleMaintenanceMode = async () => {
    setUpdatingMaintenance(true)
    try {
      const newMode = !maintenanceMode

      // Validate session before allowing any changes
      if (!session) {
        setError('No active session. Please refresh and try again.')
        setUpdatingMaintenance(false)
        return
      }

      const activeSession = await getActiveSession(session.sessionId, machineId)
      if (!activeSession) {
        alert('Your session has expired. Please scan the QR code again to continue.')
        handleSessionExpired()
        return
      }

      // Validate session access
      if (!validateSessionAccess(currentUser, userPermissions, activeSession)) {
        setError('Session validation failed. Access denied.')
        setUpdatingMaintenance(false)
        return
      }

      // Update Firestore immediately
      const updateData = {
        maintenance_mode: newMode,
        updated_at: new Date().toISOString(),
        updated_by: currentUser.email,
        session_id: session.sessionId
      }

      const success = await updateDeviceLayout(machineId, updateData)
      
      if (success) {
        // Update local state only after successful Firestore update
        setMaintenanceMode(newMode)
        setDevice(prev => ({ ...prev, maintenance_mode: newMode }))
        
        console.log(`Maintenance mode ${newMode ? 'enabled' : 'disabled'} and synced to Firestore`)
        
        if (newMode) {
          alert('Maintenance mode enabled and synced to machine. You can now edit the layout safely.')
        } else {
          alert('Maintenance mode disabled and synced to machine. Layout changes are complete.')
        }
      } else {
        alert('Failed to update maintenance mode in database. Please try again.')
      }

    } catch (error) {
      console.error('Error toggling maintenance mode:', error)
      alert('Failed to toggle maintenance mode. Please try again.')
    } finally {
      setUpdatingMaintenance(false)
    }
  }

  const renderLayoutMatrix = () => {
    const slots = tempLayoutData
    const rows = layoutRows
    
    const matrix = []
    for (let row = 0; row < rows; row++) {
      const rowCols = getRowColumnCount(row)
      const rowSlots = []
      
      // Add row indicator (simplified for replenish interface)
      rowSlots.push(
        <div key={`row-${row}-controls`} className="d-flex flex-column align-items-center justify-content-center p-2 bg-light border" style={{ minWidth: '50px' }}>
          <small className="text-muted mb-1">Row {row + 1}</small>
          <small className="text-muted">{rowCols} slots</small>
        </div>
      )
      
      // Add slot columns for this row (EXACT same logic as admin portal)
      for (let col = 0; col < rowCols; col++) {
        // Find slot for this position (same logic as admin portal)
        const slotEntry = Object.entries(slots).find(([key, slot]) => 
          slot.row === row && slot.column === col
        )
        
        const slot = slotEntry ? slotEntry[1] : null
        const slotId = slotEntry ? slotEntry[0] : null
        
        // Check if slot is locked out (handle both field name variations)
        const isLockedOut = slot && (slot.slot_lockout === true || slot.slock_lockout === true)
        const isExpired = isSlotExpired(slot)
        
        rowSlots.push(
          <div 
            key={`${row}-${col}`}
            className="position-relative"
            style={{
              width: '100px',
              height: '100px',
              minWidth: '100px',
              flexShrink: 0,
              margin: '2px',
              border: isLockedOut ? '3px solid #dc3545' : isExpired ? '3px solid #fd7e14' : '2px solid #dee2e6',
              borderRadius: '8px',
              backgroundColor: slot ? (isExpired ? '#fff3e0' : (slot.quantity === 0 ? '#ffebee' : '#e8f5e8')) : '#f8f9fa',
              cursor: maintenanceMode ? 'pointer' : 'default',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '4px',
              overflow: 'hidden'
            }}
            onClick={maintenanceMode ? () => handleSlotEdit(row, col, slotId, slot) : undefined}
          >
            {/* Lock overlay for locked out slots */}
            {isLockedOut && (
              <div 
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: 'rgba(220, 53, 69, 0.3)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: 2
                }}
              >
                <i className="fas fa-lock text-danger" style={{ fontSize: '24px' }}></i>
              </div>
            )}
            
            {isExpired && !isLockedOut && (
              <div 
                style={{
                  position: 'absolute',
                  top: '2px',
                  left: '2px',
                  zIndex: 3
                }}
              >
                <i className="fas fa-clock text-warning" style={{ fontSize: '14px' }} title={`Expired: ${slot.expiry}`}></i>
              </div>
            )}
            
            {/* Slot number indicator */}
            <div 
              style={{
                position: 'absolute',
                top: '2px',
                right: '2px',
                fontSize: '10px',
                backgroundColor: 'rgba(108, 117, 125, 0.8)',
                color: 'white',
                padding: '1px 4px',
                borderRadius: '3px',
                zIndex: 1
              }}
            >
              #{slotId || getDefaultSlotNumber(row, col)}
            </div>
            
            {slot ? (
              <>
                {/* Product Image */}
                {layoutMode === 'image' && slot.imageUrl ? (
                  <img 
                    src={slot.imageUrl} 
                    alt={slot.name}
                    style={{
                      width: '40px',
                      height: '40px',
                      objectFit: 'cover',
                      borderRadius: '4px',
                      marginBottom: '4px'
                    }}
                    onError={(e) => { e.target.style.display = 'none' }}
                  />
                ) : (
                  <i className="fas fa-cube text-muted mb-1" style={{ fontSize: '20px' }}></i>
                )}
                
                {/* Product Info */}
                <div style={{ fontSize: '9px', fontWeight: 'bold', textAlign: 'center', lineHeight: '1.1' }}>
                  {slot.name?.length > 12 ? `${slot.name.substring(0, 12)}...` : slot.name}
                </div>
                <div style={{ fontSize: '8px', color: '#666', textAlign: 'center' }}>
                  ₹{slot.price}
                </div>
                <div style={{ 
                  fontSize: '8px', 
                  color: slot.quantity === 0 ? '#dc3545' : slot.quantity < 5 ? '#fd7e14' : '#28a745',
                  fontWeight: 'bold'
                }}>
                  Qty: {slot.quantity}
                </div>
                {device?.expiry_tracking_enabled && slot.expiry && (
                  <div style={{ fontSize: '8px', color: isExpired ? '#fd7e14' : '#6c757d', fontWeight: isExpired ? 'bold' : 'normal' }}>
                    {isExpired ? 'EXP!' : `Exp: ${slot.expiry}`}
                  </div>
                )}
              </>
            ) : (
              <>
                <i className="fas fa-square text-muted mb-1" style={{ fontSize: '20px' }}></i>
                <div style={{ fontSize: '9px', color: '#6c757d', textAlign: 'center' }}>Empty Slot</div>
              </>
            )}
          </div>
        )
      }
      
      matrix.push(
        <div key={row} className="d-flex mb-2" style={{ flexWrap: 'nowrap', justifyContent: 'center', minWidth: 'fit-content' }}>
          {rowSlots}
        </div>
      )
    }
    
    return matrix
  }

  if (loading) {
    return (
      <div className="container-fluid min-vh-100 d-flex justify-content-center align-items-center">
        <LoadingSpinner />
      </div>
    )
  }

  if (error) {
    return (
      <div className="container-fluid min-vh-100 d-flex justify-content-center align-items-center">
        <div className="text-center">
          <div className="alert alert-danger" role="alert">
            <h4>Access Denied</h4>
            <p>{error}</p>
          </div>
          <button 
            className="btn btn-primary"
            onClick={() => {
              const userRoles = userPermissions?.roles || []
              const hasReplenishWorkerRole = userRoles.includes(ROLES.REPLENISH_WORKER)
              navigate(hasReplenishWorkerRole ? '/replenish-worker' : '/dashboard')
            }}
          >
            {userPermissions?.roles?.includes(ROLES.REPLENISH_WORKER)
              ? 'Return to Home' 
              : 'Return to Dashboard'}
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="container-fluid min-vh-100" style={{ backgroundColor: '#f8f9fa', paddingTop: '20px' }}>
      {/* Session Warning */}
      {showWarning && session && (
        <div className="alert alert-warning alert-dismissible fade show mx-3" role="alert">
          <strong>Session Expiring!</strong> Your session will expire in {getTimeRemaining(session)}.
          <button 
            type="button" 
            className="btn btn-sm btn-warning ms-2"
            onClick={handleExtendSession}
          >
            Extend 15 min
          </button>
          <button 
            type="button" 
            className="btn-close" 
            onClick={() => setShowWarning(false)}
          ></button>
        </div>
      )}

      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mx-3 mb-3">
        <div>
          <h4 className="mb-1">
            <i className="fas fa-tools me-2 text-primary"></i>
            Replenish: {device?.name || machineId}
          </h4>
          <small className="text-muted">
            <i className="fas fa-map-marker-alt me-1"></i>
            {device?.location || 'Location not specified'}
          </small>
        </div>
        <div className="d-flex gap-2">
          <button 
            className="btn btn-outline-secondary btn-sm"
            onClick={handleEndSession}
            disabled={endingSession}
          >
            {endingSession ? (
              <>
                <span className="spinner-border spinner-border-sm me-1" role="status"></span>
                Ending…
              </>
            ) : (
              <>
                <i className="fas fa-times me-1"></i>
                End Session
              </>
            )}
          </button>
        </div>
      </div>

      {/* Action Controls */}
      <div className="card mx-3 mb-3">
        <div className="card-header d-flex justify-content-between align-items-center">
          <h6 className="mb-0">
            <i className="fas fa-cogs me-2"></i>
            Quick Actions
          </h6>
          <span className={`badge ${maintenanceMode ? 'bg-warning text-dark' : 'bg-success'}`}>
            <i className={`fas fa-${maintenanceMode ? 'wrench' : 'check'} me-1`}></i>
            Maintenance: {maintenanceMode ? 'ON' : 'OFF'}
          </span>
        </div>
        <div className="card-body">
          {/* Maintenance Mode Toggle - Always visible */}
          <div className="row mb-3">
            <div className="col-12">
              <button
                className={`btn w-100 ${maintenanceMode ? 'btn-warning' : 'btn-outline-warning'}`}
                onClick={toggleMaintenanceMode}
                disabled={updatingMaintenance}
                title={maintenanceMode ? 'Disable maintenance mode to sync changes' : 'Enable maintenance mode to edit layout'}
              >
                {updatingMaintenance ? (
                  <>
                    <i className="fas fa-spinner fa-spin me-2"></i>
                    {maintenanceMode ? 'Disabling...' : 'Enabling...'}
                  </>
                ) : (
                  <>
                    <i className={`fas fa-${maintenanceMode ? 'power-off' : 'wrench'} me-2`}></i>
                    {maintenanceMode ? 'Disable Maintenance Mode' : 'Enable Maintenance Mode'}
                  </>
                )}
              </button>
              <small className="text-muted d-block mt-1 text-center">
                {maintenanceMode 
                  ? 'Layout editing is enabled. Disable to sync changes to machine.'
                  : 'Enable maintenance mode before making layout changes.'}
              </small>
            </div>
          </div>

          {/* Layout Editing Actions - Only visible in maintenance mode */}
          {maintenanceMode && (
            <>
              <hr />
              <div className="row">
                {/* Layout Mode Toggle */}
                <div className="col-lg-4 mb-2">
              <button
                className={`btn w-100 ${layoutMode === 'image' ? 'btn-success' : 'btn-primary'}`}
                onClick={toggleLayoutMode}
                title="Switch between image and slot number display"
              >
                <i className={`fas fa-${layoutMode === 'image' ? 'image' : 'th-large'} me-2`}></i>
                {layoutMode === 'image' ? 'Image Mode' : 'Slot Mode'}
              </button>
              <small className="text-muted d-block mt-1">
                {layoutMode === 'image' ? 'Showing product images' : 'Showing slot numbers only'}
              </small>
            </div>
            
            {/* Clear All Lockouts */}
            <div className="col-lg-4 mb-2">
              <button
                className="btn btn-outline-danger w-100"
                onClick={clearAllSlotLockouts}
                disabled={saving}
                title="Clear lockout status from all slots to make them functional"
              >
                <i className="fas fa-unlock me-2"></i>
                Clear All Lockouts
              </button>
              <small className="text-muted d-block mt-1">Remove slot restrictions</small>
            </div>
            
            {/* Restock All */}
            <div className="col-lg-4 mb-2">
              <button
                className="btn btn-outline-success w-100"
                onClick={restockAllSlots}
                disabled={saving}
                title="Restock all functional slots to their full capacity"
              >
                <i className="fas fa-boxes me-2"></i>
                Restock All Slots
              </button>
              <small className="text-muted d-block mt-1">Fill all slots to capacity</small>
            </div>
          </div>
              
              <div className="small text-muted mt-2">
                <i className="fas fa-exclamation-triangle me-1"></i>
                <strong>Note:</strong> Bulk operations automatically skip locked-out slots. Changes are applied immediately to the interface but require saving to persist.
              </div>
            </>
          )}
        </div>
      </div>

      {/* Machine Layout */}
      <div className="card mx-3 mb-3">
        <div className="card-header d-flex justify-content-between align-items-center">
          <h6 className="mb-0">
            <i className="fas fa-th-large me-2"></i>
            Machine Layout
          </h6>
          <div className="d-flex gap-2">
            <button 
              className="btn btn-success btn-sm"
              onClick={handleSaveLayout}
              disabled={saving}
            >
              {saving ? (
                <>
                  <i className="fas fa-spinner fa-spin me-1"></i>
                  Saving...
                </>
              ) : (
                <>
                  <i className="fas fa-save me-1"></i>
                  Save Layout
                </>
              )}
            </button>
          </div>
        </div>
        <div className="card-body" style={{ maxHeight: '70vh', overflow: 'auto', WebkitOverflowScrolling: 'touch' }}>
          <div className="layout-matrix" style={{ overflowX: 'auto', padding: '10px' }}>
            {renderLayoutMatrix()}
          </div>
          
          <div className="mt-3 text-center">
            <small className="text-muted">
              <i className="fas fa-info-circle me-1"></i>
              Scroll to view all slots. Tap any slot to edit with full-screen controls. Green = In Stock, Red = Out of Stock, Orange = Expired, Gray = Empty
            </small>
          </div>
        </div>
      </div>

      {/* Slot Editing Modal - Mobile Optimized */}
      {editingSlot && (
        <div 
          className="modal show d-block" 
          style={{ 
            backgroundColor: 'rgba(0,0,0,0.5)',
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 1050,
            overflow: 'auto'
          }}
        >
          <div 
            className="modal-dialog modal-dialog-centered"
            style={{
              margin: 0,
              width: '100%',
              height: '100%',
              maxWidth: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <div 
              className="modal-content"
              style={{
                width: '100%',
                maxWidth: '600px',
                margin: '10px',
                maxHeight: 'calc(100vh - 20px)',
                display: 'flex',
                flexDirection: 'column'
              }}
            >
              <div className="modal-header" style={{ flexShrink: 0 }}>
                <div>
                  <h5 className="modal-title mb-1">
                    Edit Slot {slotFormData.slotNumber}
                  </h5>
                  <small className="text-muted">
                    Row {editingSlot.row + 1}, Column {editingSlot.col + 1}
                  </small>
                </div>
                <button 
                  type="button" 
                  className="btn-close"
                  style={{ fontSize: '1.5rem', padding: '1rem' }}
                  onClick={() => {
                    setEditingSlot(null)
                    setImageUrlError('')
                  }}
                ></button>
              </div>
              <div className="modal-body" style={{ flex: 1, overflowY: 'auto' }}>
                <form>
                  <div className="mb-4">
                    <label className="form-label fw-bold" style={{ fontSize: '1.1rem' }}>Product Name</label>
                    <ProductSearchInput
                      value={slotFormData.name}
                      onProductSelect={handleProductSelect}
                      onChange={(value) => setSlotFormData({...slotFormData, name: value})}
                      placeholder="Search for a product or enter manually..."
                    />
                  </div>
                  
                  <div className="row">
                    <div className="col-6 mb-4">
                      <label className="form-label fw-bold" style={{ fontSize: '1.1rem' }}>Price (₹)</label>
                      <input
                        type="number"
                        className="form-control form-control-lg"
                        value={slotFormData.price}
                        onChange={(e) => setSlotFormData({...slotFormData, price: e.target.value})}
                        placeholder="0.00"
                        step="0.01"
                        style={{ fontSize: '1.2rem', padding: '0.75rem' }}
                      />
                    </div>
                    <div className="col-6 mb-4">
                      <label className="form-label fw-bold" style={{ fontSize: '1.1rem' }}>Quantity</label>
                      <input
                        type="number"
                        className={`form-control form-control-lg ${
                          slotFormData.capacity && slotFormData.quantity && 
                          parseInt(slotFormData.quantity) > parseInt(slotFormData.capacity) 
                            ? 'is-invalid' : ''
                        }`}
                        value={slotFormData.quantity}
                        onChange={(e) => setSlotFormData({...slotFormData, quantity: e.target.value})}
                        placeholder="0"
                        min="0"
                        style={{ fontSize: '1.2rem', padding: '0.75rem' }}
                      />
                      {slotFormData.capacity && slotFormData.quantity && 
                       parseInt(slotFormData.quantity) > parseInt(slotFormData.capacity) && (
                        <div className="invalid-feedback d-block">
                          Quantity cannot exceed capacity ({slotFormData.capacity})
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="mb-4">
                    <label className="form-label fw-bold" style={{ fontSize: '1.1rem' }}>Capacity</label>
                    <input
                      type="number"
                      className="form-control form-control-lg"
                      value={slotFormData.capacity}
                      onChange={(e) => setSlotFormData({...slotFormData, capacity: e.target.value})}
                      placeholder="Maximum capacity"
                      min="0"
                      style={{ fontSize: '1.2rem', padding: '0.75rem' }}
                    />
                  </div>

                  {device?.expiry_tracking_enabled && (
                    <div className="mb-4">
                      <label className="form-label fw-bold" style={{ fontSize: '1.1rem' }}>
                        <i className="fas fa-calendar-times me-1 text-warning"></i>
                        Expiry Date
                      </label>
                      <input
                        type="date"
                        className={`form-control form-control-lg ${slotFormData.expiry && isSlotExpired({ expiry: slotFormData.expiry }) ? 'is-invalid' : ''}`}
                        value={slotFormData.expiry}
                        onChange={(e) => setSlotFormData({...slotFormData, expiry: e.target.value})}
                        style={{ fontSize: '1.2rem', padding: '0.75rem' }}
                      />
                      {slotFormData.expiry && isSlotExpired({ expiry: slotFormData.expiry }) && (
                        <div className="invalid-feedback d-block">
                          <i className="fas fa-exclamation-triangle me-1"></i>
                          This product has expired!
                        </div>
                      )}
                    </div>
                  )}

                  <div className="mb-4">
                    <label className="form-label fw-bold" style={{ fontSize: '1.1rem' }}>Image URL (Optional)</label>
                    <input
                      type="url"
                      className={`form-control form-control-lg ${imageUrlError ? 'is-invalid' : ''}`}
                      value={slotFormData.imageUrl}
                      onChange={(e) => {
                        const newUrl = e.target.value
                        setSlotFormData({...slotFormData, imageUrl: newUrl})
                        const validation = validateImageUrl(newUrl)
                        setImageUrlError(validation.error)
                      }}
                      placeholder="https://example.com/image.jpg"
                      style={{ fontSize: '1rem', padding: '0.75rem' }}
                    />
                    {imageUrlError && (
                      <div className="invalid-feedback d-block">
                        <i className="fas fa-exclamation-triangle me-1"></i>
                        {imageUrlError}
                      </div>
                    )}
                    <small className="text-muted">
                      Only HTTP/HTTPS URLs are accepted. Data URIs are not allowed.
                    </small>
                  </div>
                </form>
              </div>
              <div className="modal-footer" style={{ flexShrink: 0, gap: '0.5rem', padding: '1rem' }}>
                <button 
                  type="button" 
                  className="btn btn-secondary btn-lg"
                  style={{ minHeight: '50px', flex: 1 }}
                  onClick={() => {
                    setEditingSlot(null)
                    setImageUrlError('')
                  }}
                >
                  <i className="fas fa-times me-2"></i>
                  Cancel
                </button>
                {editingSlot.slotId && (
                  <button 
                    type="button" 
                    className="btn btn-danger btn-lg"
                    style={{ minHeight: '50px', flex: 1 }}
                    onClick={handleSlotDelete}
                  >
                    <i className="fas fa-trash me-2"></i>
                    Delete
                  </button>
                )}
                <button 
                  type="button" 
                  className="btn btn-primary btn-lg"
                  style={{ minHeight: '50px', flex: 1 }}
                  onClick={handleSlotSave}
                  disabled={
                    imageUrlError || 
                    (slotFormData.capacity && slotFormData.quantity && 
                     parseInt(slotFormData.quantity) > parseInt(slotFormData.capacity))
                  }
                >
                  <i className="fas fa-save me-2"></i>
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default ReplenishInterface